import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Home, MessageSquare, FileText, Receipt, Sparkles, ArrowRight, Calendar,
  ChevronRight, CheckCircle2, AlertTriangle, CalendarClock, ShieldAlert,
} from "lucide-react";
import AppShell, { getActiveSchemeId, setActiveSchemeId } from "@/components/AppShell";
import { useAuth } from "@/auth/AuthContext";
import { api, apiError } from "@/lib/api";
import OwnerActivityFeed from "@/pages/cockpits/OwnerActivityFeed";

const $ = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function OwnerPortal() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        let sid = getActiveSchemeId();
        if (!sid) {
          const { data: list } = await api.get("/schemes");
          if (!list.length) { navigate("/onboarding"); return; }
          sid = list[0].scheme_id; setActiveSchemeId(sid);
        }
        const { data: payload } = await api.get(`/schemes/${sid}/owner-portal`);
        setData(payload);
      } catch (e) { toast.error(apiError(e)); }
    })();
  }, [navigate]);

  if (!data) {
    return <AppShell><div className="max-w-5xl mx-auto px-5 py-10 text-sm" style={{ color: "var(--slate)" }}>Loading…</div></AppShell>;
  }

  const { scheme, my_lots, my_levies, messages, published_minutes, document_count } = data;
  const outstandingBand = my_levies.overdue_count > 0 ? "var(--alert)" : my_levies.outstanding_total > 0 ? "var(--warning)" : "var(--success)";

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto px-5 lg:px-8 py-8 lg:py-10" data-testid="owner-portal">
        {/* Hero — scheme-specific plan-number badge treatment (Demo Playability sprint papercut #3) */}
        <div className="rounded-2xl p-6 lg:p-8 mb-6" style={{ background: "linear-gradient(135deg, var(--navy) 0%, #16243a 100%)", color: "white" }}>
          <div className="flex items-center gap-2 mb-3">
            <span
              className="inline-flex items-center gap-1.5 text-[10px] font-mono font-bold uppercase tracking-[0.15em] px-2 py-1 rounded"
              style={{ background: "rgba(255,255,255,0.14)", color: "white", border: "1px solid rgba(255,255,255,0.25)" }}
              data-testid="owner-plan-badge"
            >
              {scheme.plan_number}
            </span>
            <span className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: "rgba(255,255,255,0.55)" }}>
              {scheme.state} · Owner portal
            </span>
          </div>
          <h1 className="text-2xl lg:text-3xl font-bold" data-testid="owner-greeting">
            Hi {user?.name?.split(" ")[0] || "there"}.
          </h1>
          <p className="text-sm mt-1 font-medium" style={{ color: "rgba(255,255,255,0.9)" }}>
            {scheme.name}
          </p>
        </div>

        {/* Owner UX Polish — 3 forward-looking hero cards. Data comes from
            /owner-portal aggregate (next_levy_due, next_meeting, next_compliance_deadline). */}
        <ForwardHero
          nextLevy={data?.next_levy_due}
          nextMeeting={data?.next_meeting}
          nextCompliance={data?.next_compliance_deadline}
        />

        {/* Home Pages Rework — activity feed sits below the 3 hero cards */}
        <OwnerActivityFeed schemeId={scheme?.scheme_id} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
          {/* Your lots + outstanding */}
          <div className="rounded-2xl border bg-white p-5" style={{ borderColor: "var(--border)" }} data-testid="owner-lots-card">
            <div className="text-[10px] font-bold uppercase tracking-wider mb-3" style={{ color: "var(--slate)" }}>Your lots</div>
            <div className="flex items-baseline gap-3 mb-3">
              <div className="text-3xl font-bold tabular-nums" style={{ color: "var(--navy)" }} data-testid="owner-lots-count">{my_lots.length}</div>
              <div className="text-xs" style={{ color: "var(--slate)" }}>{my_lots.map((l) => `Lot ${l.lot_number}`).join(" · ")}</div>
            </div>
            <div className="pt-3 border-t" style={{ borderColor: "var(--border)" }}>
              <div className="flex items-baseline justify-between">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "var(--slate)" }}>Outstanding levies</div>
                  <div className="text-2xl font-bold tabular-nums" style={{ color: outstandingBand }} data-testid="owner-outstanding">{$(my_levies.outstanding_total)}</div>
                </div>
                {my_levies.overdue_count > 0 && (
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ background: "var(--alert-light)", color: "var(--alert)" }} data-testid="owner-overdue-badge">
                    {my_levies.overdue_count} overdue
                  </span>
                )}
              </div>
              <Link to="/scheme/levies" className="inline-flex items-center text-xs font-semibold mt-2 hover:underline" style={{ color: "var(--blue-dark)" }} data-testid="owner-levies-link">
                View levy notices <ChevronRight size={12}/>
              </Link>
            </div>
          </div>

          {/* Messages */}
          <div className="rounded-2xl border bg-white p-5" style={{ borderColor: "var(--border)" }} data-testid="owner-messages-card">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "var(--slate)" }}>Messages</div>
              {messages.unread_total > 0 && (
                <span className="text-[10px] font-bold tabular-nums px-1.5 py-0.5 rounded-full text-white" style={{ background: "var(--blue)" }} data-testid="owner-unread-total">
                  {messages.unread_total}
                </span>
              )}
            </div>
            <div className="space-y-2" data-testid="owner-recent-messages">
              {messages.recent.length === 0 ? (
                <p className="text-xs" style={{ color: "var(--tagline-grey)" }}>No messages yet.</p>
              ) : messages.recent.slice(0, 3).map((m) => (
                <Link key={m.thread_id} to="/scheme/messages" className="block py-1.5 px-2 -mx-2 rounded hover:bg-slate-50">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: "var(--slate-light)", color: "var(--slate)" }}>
                      {m.thread_kind}
                    </span>
                    <span className="text-xs font-semibold truncate" style={{ color: "var(--navy)" }}>{m.thread_title}</span>
                    {m.unread > 0 && <span className="text-[10px] font-bold text-white px-1.5 py-0.5 rounded-full" style={{ background: "var(--blue)" }}>{m.unread}</span>}
                  </div>
                  <div className="text-xs mt-0.5 truncate" style={{ color: "var(--slate)" }}>{m.preview}</div>
                </Link>
              ))}
            </div>
            <Link to="/scheme/messages" className="inline-flex items-center text-xs font-semibold mt-3 hover:underline" style={{ color: "var(--blue-dark)" }} data-testid="owner-messages-link">
              Open messages <ChevronRight size={12}/>
            </Link>
          </div>

          {/* Documents */}
          <div className="rounded-2xl border bg-white p-5" style={{ borderColor: "var(--border)" }} data-testid="owner-docs-card">
            <div className="text-[10px] font-bold uppercase tracking-wider mb-3" style={{ color: "var(--slate)" }}>Documents</div>
            <div className="text-3xl font-bold tabular-nums" style={{ color: "var(--navy)" }} data-testid="owner-doc-count">{document_count}</div>
            <div className="text-xs mt-1" style={{ color: "var(--slate)" }}>visible to you (insurance, financials, by-laws, certificates…)</div>
            <Link to="/scheme/documents" className="inline-flex items-center text-xs font-semibold mt-3 hover:underline" style={{ color: "var(--blue-dark)" }} data-testid="owner-docs-link">
              Open documents <ChevronRight size={12}/>
            </Link>
          </div>
        </div>

        {/* Ask CTA + minutes */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Link to="/scheme/ask" className="lg:col-span-1 rounded-2xl p-5 flex flex-col justify-between transition-shadow hover:shadow-md" style={{ background: "var(--blue)", color: "white" }} data-testid="owner-ask-cta">
            <div>
              <Sparkles size={16} className="mb-2"/>
              <div className="text-base font-bold">Ask a question</div>
              <p className="text-xs mt-1" style={{ color: "rgba(255,255,255,0.85)" }}>Plain-English answers grounded in your scheme&apos;s state and recent activity.</p>
            </div>
            <div className="inline-flex items-center gap-1 text-xs font-semibold mt-3">Open <ArrowRight size={12}/></div>
          </Link>
          <div className="lg:col-span-2 rounded-2xl border bg-white p-5" style={{ borderColor: "var(--border)" }} data-testid="owner-minutes-card">
            <div className="flex items-center gap-2 mb-3">
              <Calendar size={14} style={{ color: "var(--blue-dark)" }}/>
              <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "var(--slate)" }}>Latest published minutes</div>
            </div>
            {data.insurance && (
              <div className="text-[11px] pb-3 mb-3 border-b" style={{ borderColor: "var(--border)", color: "var(--slate)" }} data-testid="owner-insurance-line">
                <strong style={{ color: "var(--navy)" }}>Insurance:</strong>{" "}
                {data.insurance.active_count} active polic{data.insurance.active_count === 1 ? "y" : "ies"} covering this scheme.{" "}
                {data.insurance.next_renewal_days != null ? `Next renewal in ${data.insurance.next_renewal_days}d.` : "No upcoming renewals."}
              </div>
            )}
            {published_minutes.length === 0 ? (
              <p className="text-xs" style={{ color: "var(--tagline-grey)" }}>No published minutes yet.</p>
            ) : published_minutes.map((p) => (
              <Link key={p.meeting_id} to={`/scheme/meetings/${p.meeting_id}`} className="block py-2 border-b last:border-b-0 hover:bg-slate-50 -mx-2 px-2 rounded" style={{ borderColor: "var(--border)" }}>
                <div className="text-sm font-semibold" style={{ color: "var(--navy)" }}>{p.title}</div>
                <div className="text-[11px] mt-0.5" style={{ color: "var(--slate)" }}>
                  {p.meeting_type} · {new Date(p.scheduled_at).toLocaleDateString()} · published {new Date(p.published_at).toLocaleDateString()}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </AppShell>
  );
}

// Owner UX Polish — forward-looking hero cards. All 3 gracefully handle a
// null upstream field (all levies paid / no meeting / no compliance due).
function ForwardHero({ nextLevy, nextMeeting, nextCompliance }) {
  const daysUntil = (iso) => {
    if (!iso) return null;
    const now = new Date();
    const target = new Date(iso);
    return Math.round((target - now) / 86400000);
  };
  const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const levyOverdue = nextLevy?.status === "overdue";
  const cardBase = "rounded-2xl border bg-white p-5 flex flex-col justify-between min-h-[160px]";

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6" data-testid="owner-forward-hero">
      {/* Card 1 — Next levy */}
      <div
        className={cardBase}
        style={{ borderColor: levyOverdue ? "var(--alert)" : "var(--border)", borderWidth: levyOverdue ? 2 : 1 }}
        data-testid="hero-next-levy"
      >
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "var(--slate)" }}>Next levy due</span>
            {nextLevy && (
              <span
                className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                style={{
                  background: levyOverdue ? "var(--alert-light)" : "#dbeafe",
                  color:      levyOverdue ? "var(--alert)"       : "#1e40af",
                }}
              >
                {nextLevy.status}
              </span>
            )}
          </div>
          {nextLevy ? (
            <>
              <div className="text-3xl font-bold tabular-nums mt-2" style={{ color: levyOverdue ? "var(--alert)" : "var(--navy)" }} data-testid="hero-levy-amount">
                {money(nextLevy.amount)}
              </div>
              <div className="text-xs mt-1" style={{ color: "var(--slate)" }}>
                Due {nextLevy.due_date}{daysUntil(nextLevy.due_date) !== null && (levyOverdue ? ` · ${Math.abs(daysUntil(nextLevy.due_date))} days overdue` : ` · ${daysUntil(nextLevy.due_date)} days away`)}
              </div>
            </>
          ) : (
            <div className="mt-3 flex items-center gap-2" data-testid="hero-levy-clear">
              <CheckCircle2 size={20} style={{ color: "#166534" }}/>
              <span className="text-sm font-medium" style={{ color: "var(--navy)" }}>All levies paid — you&apos;re up to date</span>
            </div>
          )}
        </div>
        {nextLevy && (
          <Link
            to="/scheme/levies"
            className="inline-flex items-center gap-1 text-xs font-semibold mt-3 hover:underline"
            style={{ color: levyOverdue ? "var(--alert)" : "var(--blue-dark)" }}
            data-testid="hero-levy-cta"
          >
            {levyOverdue ? "Pay now" : "View levy notices"} <ChevronRight size={12}/>
          </Link>
        )}
      </div>

      {/* Card 2 — Next meeting */}
      <div className={cardBase} style={{ borderColor: "var(--border)" }} data-testid="hero-next-meeting">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <CalendarClock size={12} style={{ color: "var(--slate)" }}/>
            <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "var(--slate)" }}>Next meeting</span>
          </div>
          {nextMeeting ? (
            <>
              <div className="text-xl font-bold mt-2" style={{ color: "var(--navy)" }} data-testid="hero-meeting-type">
                {nextMeeting.meeting_type} · {(nextMeeting.scheduled_at || "").slice(0, 10) || "TBC"}
              </div>
              <div className="text-xs mt-1" style={{ color: "var(--slate)" }}>
                {daysUntil(nextMeeting.scheduled_at) !== null ? `${daysUntil(nextMeeting.scheduled_at)} days away` : "date TBC"} · {nextMeeting.status === "notice_sent" ? "notice sent" : "awaiting notice"}
              </div>
            </>
          ) : (
            <div className="mt-3 text-sm" style={{ color: "var(--slate)" }} data-testid="hero-meeting-none">
              No meeting scheduled — the committee will notify you.
            </div>
          )}
        </div>
        {nextMeeting && (
          <Link
            to={`/scheme/meetings/${nextMeeting.meeting_id}`}
            className="inline-flex items-center gap-1 text-xs font-semibold mt-3 hover:underline"
            style={{ color: "var(--blue-dark)" }}
            data-testid="hero-meeting-cta"
          >
            View meeting notice <ChevronRight size={12}/>
          </Link>
        )}
      </div>

      {/* Card 3 — Next compliance deadline */}
      <div className={cardBase} style={{ borderColor: "var(--border)" }} data-testid="hero-next-compliance">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <ShieldAlert size={12} style={{ color: "var(--slate)" }}/>
            <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "var(--slate)" }}>Next compliance deadline</span>
          </div>
          {nextCompliance ? (
            <>
              <div className="text-xl font-bold mt-2" style={{ color: "var(--navy)" }} data-testid="hero-compliance-name">
                {nextCompliance.name}
              </div>
              <div className="text-xs mt-1" style={{ color: "var(--slate)" }}>
                Due {nextCompliance.due_date}{daysUntil(nextCompliance.due_date) !== null && ` · ${daysUntil(nextCompliance.due_date)} days`}
              </div>
            </>
          ) : (
            <div className="mt-3 text-sm" style={{ color: "var(--slate)" }} data-testid="hero-compliance-none">
              No compliance items due in the next 90 days.
            </div>
          )}
        </div>
        {nextCompliance && (
          <Link
            to="/scheme/compliance"
            className="inline-flex items-center gap-1 text-xs font-semibold mt-3 hover:underline"
            style={{ color: "var(--blue-dark)" }}
            data-testid="hero-compliance-cta"
          >
            View compliance register <ChevronRight size={12}/>
          </Link>
        )}
      </div>
    </div>
  );
}
